// Fixed, image-relative resolution: independent of battle grid, viewport and zoom.
export const FOG_SIZE = 1024;
export const FOG_BYTES = FOG_SIZE * FOG_SIZE / 8;
export const revealed = (mask, x, y) => {
  if (!mask || x < 0 || y < 0 || x >= 1 || y >= 1) return false;
  const index = Math.floor(y * FOG_SIZE) * FOG_SIZE + Math.floor(x * FOG_SIZE);
  return Boolean(mask[index >> 3] & (1 << (index & 7)));
};

// Swept ellipse in normalized coordinates. rx/ry preserve a circular brush on rectangular maps.
export function paintFog(mask, { points, rx, ry }) {
  const changed = new Map();
  for (let i = 0; i < points.length; i++) {
    const a = points[Math.max(0, i - 1)], b = points[i];
    const minX = Math.max(0, Math.floor((Math.min(a.x, b.x) - rx) * FOG_SIZE));
    const maxX = Math.min(FOG_SIZE - 1, Math.ceil((Math.max(a.x, b.x) + rx) * FOG_SIZE));
    const minY = Math.max(0, Math.floor((Math.min(a.y, b.y) - ry) * FOG_SIZE));
    const maxY = Math.min(FOG_SIZE - 1, Math.ceil((Math.max(a.y, b.y) + ry) * FOG_SIZE));
    const dx = (b.x - a.x) / rx, dy = (b.y - a.y) / ry, length = dx * dx + dy * dy;
    for (let y = minY; y <= maxY; y++) for (let x = minX; x <= maxX; x++) {
      const px = ((x + .5) / FOG_SIZE - a.x) / rx, py = ((y + .5) / FOG_SIZE - a.y) / ry;
      const t = length ? Math.max(0, Math.min(1, (px * dx + py * dy) / length)) : 0;
      if ((px - t * dx) ** 2 + (py - t * dy) ** 2 > 1) continue;
      const index = y * FOG_SIZE + x, offset = index >> 3, value = mask[offset] | (1 << (index & 7));
      if (value !== mask[offset]) { mask[offset] = value; changed.set(offset, value); }
    }
  }
  return [...changed];
}

// Square fog painting: the DM reveals or re-covers whole grid squares. Each square owns the fog pixels from its
// rounded left/top edge up to the next square's, so neighbouring squares never share a pixel.
export const FOG_CELL_LIMIT = 256;
export const fogCellBounds = (columns, rows, x, y) => ({
  left: Math.max(0, Math.round(x / columns * FOG_SIZE)),
  right: Math.min(FOG_SIZE, Math.round((x + 1) / columns * FOG_SIZE)) - 1,
  top: Math.max(0, Math.round(y / rows * FOG_SIZE)),
  bottom: Math.min(FOG_SIZE, Math.round((y + 1) / rows * FOG_SIZE)) - 1,
});

/** Reveal (or, with hide, cover again) whole squares. Returns the changed mask bytes as [offset, value]. */
export function paintFogCells(mask, { cells, columns, rows, hide = false }) {
  const changed = new Map();
  for (const { x, y } of cells) {
    const { left, right, top, bottom } = fogCellBounds(columns, rows, x, y);
    for (let py = top; py <= bottom; py++) for (let px = left; px <= right; px++) {
      const index = py * FOG_SIZE + px, offset = index >> 3, bit = 1 << (index & 7);
      const value = hide ? mask[offset] & ~bit : mask[offset] | bit;
      if (value !== mask[offset]) { mask[offset] = value; changed.set(offset, value); }
    }
  }
  return [...changed];
}

/** Apply a queued fog stroke of either kind: square cells, or a freehand sweep from older clients. */
export const paintFogStroke = (mask, stroke) => (stroke.kind === 'cells' ? paintFogCells(mask, stroke) : paintFog(mask, stroke));

export function tokenRevealed(mask, token, map) {
  const categorySize = { tiny: 1, small: 1, medium: 1, large: 2, huge: 3, gargantuan: 4 }[String(token.size || token.creatureSize || '').toLowerCase()] || 1;
  const size = Math.max(1, Math.floor(Number(token.gridSize || token.occupiedSquares) || categorySize));
  const columns = Number(map?.grid?.columns || map?.gridColumns) || 24;
  const rows = Number(map?.grid?.rows || map?.gridRows) || 24;
  const x = Number(token.position?.x ?? token.x), y = Number(token.position?.y ?? token.y);
  if (!Number.isFinite(x) || !Number.isFinite(y) || !mask) return false;
  // Conservatively check the entire occupied footprint, including large creatures.
  const anchorX = Math.floor(x * columns), anchorY = Math.floor(y * rows);
  // The same pixels square painting covers, so a token on revealed squares is visible.
  const { left, top } = fogCellBounds(columns, rows, anchorX, anchorY);
  const { right, bottom } = fogCellBounds(columns, rows, anchorX + size - 1, anchorY + size - 1);
  if (x < 0 || x >= 1 || y < 0 || y >= 1) return false;
  for (let py = top; py <= bottom; py++) for (let px = left; px <= right; px++) {
    const index = py * FOG_SIZE + px;
    if (!(mask[index >> 3] & (1 << (index & 7)))) return false;
  }
  return true;
}
